import { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import CopyButton from "@/components/CopyButton";
import { countryNames } from "@/lib/countries";

const C = "/admin/console";

function Card({ title, sub, children, testid }) {
  return (
    <div className="k-container" data-testid={testid} style={{ padding: 20, marginBottom: 16 }}>
      <h3 className="k-heading" style={{ fontSize: 16, marginTop: 0 }}>{title}</h3>
      {sub && <p className="k-muted" style={{ fontSize: 12, marginTop: 0 }}>{sub}</p>}
      {children}
    </div>
  );
}

function KV({ obj }) {
  if (!obj || Object.keys(obj).length === 0) return <p className="k-muted" style={{ fontSize: 13 }}>None yet.</p>;
  return (
    <table className="k-table"><tbody>
      {Object.entries(obj).map(([k, v]) => (
        <tr key={k}><td className="k-heading">{k}</td><td className="k-muted">{typeof v === "object" ? JSON.stringify(v) : String(v)}</td></tr>
      ))}
    </tbody></table>
  );
}

// ---------------- A1 Verification Queue ----------------
function VerifyQueue() {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ patch: "", region: "", sector: "", access_class: "" });
  const [bulk, setBulk] = useState({ region: "", sector: "", access_class: "", id_prefix: "", reason: "" });
  const load = () => api.get(`${C}/verify-queue`, { params: { ...f, limit: 50 } }).then(r => setData(r.data)).catch(e => toast.error(errMsg(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const act = async (id, kind) => {
    try {
      if (kind === "verify") await api.post(`${C}/verify-queue/${id}/verify`);
      else {
        const reason = window.prompt(kind === "reject" ? "Reason to REJECT (removes from catalogue):" : "Correction reason (fed back to the note):");
        if (!reason) return;
        await api.post(`${C}/verify-queue/${id}/${kind}`, { reason });
      }
      toast.success(`${id}: ${kind}`); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const doBulk = async () => {
    try { const r = await api.post(`${C}/verify-queue/bulk-approve`, bulk); toast.success(`Approved ${r.data.approved}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <Card title="A1 · Connector Verification Queue" sub={data?.note} testid="a1-verify-queue">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <input className="k-control" data-testid="vq-patch" placeholder="id prefix (e.g. K12)" style={{ maxWidth: 150 }} value={f.patch} onChange={e => setF({ ...f, patch: e.target.value })} />
          <input className="k-control" data-testid="vq-region" placeholder="region code (GB)" style={{ maxWidth: 130 }} value={f.region} onChange={e => setF({ ...f, region: e.target.value })} />
          <input className="k-control" data-testid="vq-sector" placeholder="sector" style={{ maxWidth: 130 }} value={f.sector} onChange={e => setF({ ...f, sector: e.target.value })} />
          <input className="k-control" data-testid="vq-class" placeholder="class (A/B/C)" style={{ maxWidth: 110 }} value={f.access_class} onChange={e => setF({ ...f, access_class: e.target.value })} />
          <button className="k-btn k-btn-primary" data-testid="vq-filter" onClick={load}>Filter</button>
        </div>
        <p className="k-muted" style={{ fontSize: 13 }} data-testid="vq-total">{data?.total_unverified ?? "—"} unverified · showing {data?.showing ?? 0}</p>
        <div style={{ maxHeight: 360, overflowY: "auto" }}>
          <table className="k-table">
            <thead><tr><th>ID</th><th>Name</th><th>Jur</th><th>Sector</th><th>Class</th><th>Actions</th></tr></thead>
            <tbody>
              {data?.items?.map(e => (
                <tr key={e.id} data-testid={`vq-row-${e.id}`}>
                  <td className="k-heading">{e.id}</td>
                  <td className="k-muted" style={{ maxWidth: 220 }}>{e.name}{e.needs_correction && <span className="k-tag-warn" style={{ marginLeft: 6, fontSize: 10 }}>needs correction</span>}</td>
                  <td className="k-muted">{countryNames(e.jurisdiction || [])}</td>
                  <td className="k-muted">{e.sector || "—"}</td>
                  <td>{e.access_class}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="k-btn k-btn-ghost" data-testid={`vq-verify-${e.id}`} style={{ padding: "4px 8px", fontSize: 12 }} onClick={() => act(e.id, "verify")}>Verify</button>{" "}
                    <button className="k-btn k-btn-ghost" data-testid={`vq-correct-${e.id}`} style={{ padding: "4px 8px", fontSize: 12 }} onClick={() => act(e.id, "needs-correction")}>Correct</button>{" "}
                    <button className="k-btn k-btn-ghost" data-testid={`vq-reject-${e.id}`} style={{ padding: "4px 8px", fontSize: 12 }} onClick={() => act(e.id, "reject")}>Reject</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Card title="A1 · Bulk approve by source batch" sub="Deliberate, logged action — never a default. Provide at least one filter." testid="a1-bulk">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input className="k-control" data-testid="bulk-prefix" placeholder="id prefix (K12)" style={{ maxWidth: 130 }} value={bulk.id_prefix} onChange={e => setBulk({ ...bulk, id_prefix: e.target.value })} />
          <input className="k-control" data-testid="bulk-region" placeholder="region" style={{ maxWidth: 110 }} value={bulk.region} onChange={e => setBulk({ ...bulk, region: e.target.value })} />
          <input className="k-control" data-testid="bulk-sector" placeholder="sector" style={{ maxWidth: 110 }} value={bulk.sector} onChange={e => setBulk({ ...bulk, sector: e.target.value })} />
          <input className="k-control" data-testid="bulk-reason" placeholder="reason (required)" style={{ flex: 1, minWidth: 180 }} value={bulk.reason} onChange={e => setBulk({ ...bulk, reason: e.target.value })} />
          <button className="k-btn k-btn-primary" data-testid="bulk-approve" onClick={doBulk}>Bulk approve</button>
        </div>
      </Card>
    </>
  );
}

// ---------------- Catalogue & Data ----------------
function CatalogueData() {
  const [tax, setTax] = useState(null);
  const [cov, setCov] = useState(null);
  const [excl, setExcl] = useState([]);
  const [audit, setAudit] = useState(null);
  const [live, setLive] = useState(null);
  const [hist, setHist] = useState([]);
  const [q, setQ] = useState("");
  const [ne, setNe] = useState({ name: "", reason: "", reviewing_authority: "" });
  const load = () => {
    api.get(`${C}/region-taxonomy`).then(r => setTax(r.data)).catch(() => {});
    api.get(`${C}/coverage-map`).then(r => setCov(r.data)).catch(() => {});
    api.get(`${C}/excluded-entities`).then(r => setExcl(r.data.entities || [])).catch(() => {});
    api.get(`${C}/consistency-audit`).then(r => setAudit(r.data)).catch(() => {});
    api.get(`${C}/connector-liveness`).then(r => setLive(r.data)).catch(() => {});
    api.get(`${C}/patch-history`).then(r => setHist(r.data.history || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const addExcluded = async () => {
    try { await api.post(`${C}/excluded-entities`, ne); toast.success("Added"); setNe({ name: "", reason: "", reviewing_authority: "" }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const removeExcluded = async (id) => {
    const reason = window.prompt("This reverses a sanctions/human-rights decision. State a logged reason:");
    if (!reason) return;
    try { await api.post(`${C}/excluded-entities/${id}/remove`, { reason }); toast.success("Removed (logged)"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const runLiveness = async () => {
    try { const r = await api.post(`${C}/connector-liveness/run`, { limit: 25 }); toast.success(`Checked ${r.data.checked}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const searchHist = () => api.get(`${C}/patch-history`, { params: { q } }).then(r => setHist(r.data.history || [])).catch(() => {});
  return (
    <>
      <Card title="A12 · Excluded-entity registry" sub="Authoritative list of permanently excluded connectors. Removal requires a logged, attributed reason." testid="a12-excluded">
        <table className="k-table">
          <thead><tr><th>Name</th><th>Reason</th><th>Authority</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {excl.map(e => (
              <tr key={e.id} data-testid={`excl-${e.id}`}>
                <td className="k-heading">{e.name}</td><td className="k-muted">{e.reason}</td>
                <td className="k-muted">{e.reviewing_authority}</td>
                <td>{e.status === "excluded" ? <span className="k-tag-red">excluded</span> : <span className="k-tag-warn">removed</span>}</td>
                <td>{e.status === "excluded" && <button className="k-btn k-btn-ghost" data-testid={`excl-remove-${e.id}`} style={{ padding: "4px 8px", fontSize: 12 }} onClick={() => removeExcluded(e.id)}>Remove</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
          <input className="k-control" data-testid="excl-name" placeholder="name" style={{ maxWidth: 160 }} value={ne.name} onChange={e => setNe({ ...ne, name: e.target.value })} />
          <input className="k-control" data-testid="excl-reason" placeholder="reason" style={{ flex: 1, minWidth: 160 }} value={ne.reason} onChange={e => setNe({ ...ne, reason: e.target.value })} />
          <input className="k-control" data-testid="excl-authority" placeholder="reviewing authority" style={{ maxWidth: 190 }} value={ne.reviewing_authority} onChange={e => setNe({ ...ne, reviewing_authority: e.target.value })} />
          <button className="k-btn k-btn-primary" data-testid="excl-add" onClick={addExcluded}>Add</button>
        </div>
      </Card>

      <Card title="A4 · Global catalogue coverage map" sub={cov?.note} testid="a4-coverage">
        <div style={{ maxHeight: 300, overflowY: "auto" }}>
          <table className="k-table">
            <thead><tr><th>Code</th><th>Country</th><th>Declared depth</th><th>Live connectors</th></tr></thead>
            <tbody>
              {cov?.countries?.map((c, i) => (
                <tr key={i}><td className="k-heading">{c.code}</td><td className="k-muted">{c.name}</td>
                  <td>{c.declared_depth === "zero" ? <span className="k-tag-warn">zero</span> : String(c.declared_depth)}</td>
                  <td className="k-heading">{c.live_connectors}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="A2 · Region taxonomy — live connector counts" sub={tax?.note} testid="a2-taxonomy">
        <KV obj={tax?.live_connector_counts} />
      </Card>

      <Card title="A13 · Cross-patch consistency auditor" sub={audit?.note} testid="a13-consistency">
        <p className="k-muted" style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
          {audit?.drift && <AlertTriangle size={13} aria-hidden="true" />}
          Drift: {audit ? (audit.drift ? "appendices without a console home" : "none — all appendices wired") : "…"}
        </p>
        {audit?.unwired_appendices?.length > 0 && <KV obj={audit.unwired_appendices.reduce((a, f) => ({ ...a, [f]: "no console home" }), {})} />}
      </Card>

      <Card title="A18 · Connector liveness auto-flagger" sub={live?.note} testid="a18-liveness">
        <p className="k-muted" style={{ fontSize: 13 }}>Checked {live?.checked ?? 0} · dead links {live?.dead_links ?? 0}</p>
        <button className="k-btn k-btn-ghost" data-testid="liveness-run" onClick={runLiveness}>Run reachability check (25)</button>
      </Card>

      <Card title="A20 · Patch history viewer" sub="Searchable build-prompt history, inside the console." testid="a20-patch-history">
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <input className="k-control" data-testid="hist-q" placeholder="search patches…" value={q} onChange={e => setQ(e.target.value)} />
          <button className="k-btn k-btn-ghost" data-testid="hist-search" onClick={searchHist}>Search</button>
        </div>
        {hist.map(h => (
          <div key={h.id} className="k-control" style={{ marginBottom: 8 }} data-testid={`patch-${h.seq}`}>
            <div className="k-heading" style={{ fontSize: 13, fontWeight: 600 }}>{h.patch} — {h.title}</div>
            <div className="k-muted" style={{ fontSize: 12 }}>{h.why}</div>
          </div>
        ))}
      </Card>
    </>
  );
}

// ---------------- Journey Analytics ----------------
function JourneyAnalytics() {
  const [d, setD] = useState({});
  useEffect(() => {
    const eps = { funnel: "analytics/funnel", satisfaction: "analytics/satisfaction", layers: "analytics/layer-usage",
      blueprints: "analytics/blueprint-usage", switch: "analytics/switch-market", seats: "analytics/reviewer-seats",
      dropoff: "analytics/perimeter-dropoff", artifacts: "artifacts-log", shares: "share-links", readiness: "market-readiness", honesty: "honesty-content" };
    Object.entries(eps).forEach(([k, p]) => api.get(`${C}/${p}`).then(r => setD(x => ({ ...x, [k]: r.data }))).catch(() => {}));
  }, []);
  const revokeShare = async (t) => { try { await api.post(`${C}/share-links/${t}/revoke`); toast.success("Revoked"); api.get(`${C}/share-links`).then(r => setD(x => ({ ...x, shares: r.data }))); } catch (e) { toast.error(errMsg(e)); } };
  const annotate = async (region) => {
    const note = window.prompt(`Admin annotation for ${region} (shown as admin-adjusted, never silent):`);
    if (!note) return;
    try { await api.post(`${C}/market-readiness/annotate`, { region, note }); toast.success("Annotated"); api.get(`${C}/market-readiness`).then(r => setD(x => ({ ...x, readiness: r.data }))); } catch (e) { toast.error(errMsg(e)); }
  };
  const addHonesty = async () => {
    const text = window.prompt("New 'what Kadana will not do yet' item:");
    if (!text) return;
    try { await api.post(`${C}/honesty-content`, { text }); toast.success("Added"); api.get(`${C}/honesty-content`).then(r => setD(x => ({ ...x, honesty: r.data }))); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <Card title="A16 · Onboarding funnel" sub={d.funnel?.note} testid="a16-funnel">
        <table className="k-table"><tbody>{d.funnel?.steps?.map((s, i) => <tr key={i}><td className="k-heading">{s.step}</td><td className="k-muted">{s.count}</td></tr>)}</tbody></table>
      </Card>
      <Card title="A17 · Satisfaction (internal sector breakdown)" sub={d.satisfaction?.note} testid="a17-satisfaction">
        <p className="k-heading" style={{ fontSize: 14 }}>Overall: {d.satisfaction?.overall?.positive_pct ?? 0}% of {d.satisfaction?.overall?.responses ?? 0}</p>
        <KV obj={d.satisfaction?.by_domain} />
      </Card>
      <Card title="A5 · Compliance layer usage" sub={d.layers?.note} testid="a5-layers"><KV obj={d.layers?.layer_relevance_counts} /></Card>
      <Card title="A6 · Blueprint usage by domain" sub={d.blueprints?.note} testid="a6-blueprints"><KV obj={d.blueprints?.projects_by_domain} /></Card>
      <Card title="A7 · Switch Market audit & margin" sub={d.switch?.note} testid="a7-switch">
        <p className="k-muted" style={{ fontSize: 13 }}>Switches: {d.switch?.switch_build_count ?? 0} · revenue (credits): {d.switch?.switch_revenue_credits ?? 0}</p>
        <KV obj={d.switch?.top_region_pairs} />
      </Card>
      <Card title="A8 · Passport / Build Badge issuance monitor" sub={d.artifacts?.note} testid="a8-artifacts">
        <p className="k-muted" style={{ fontSize: 13 }}>Builds {d.artifacts?.builds_total ?? 0} · launched {d.artifacts?.projects_launched ?? 0} · badge-eligible-without-launch anomalies: <strong>{d.artifacts?.badge_eligible_without_launch ?? 0}</strong></p>
      </Card>
      <Card title="A9 · Investor/auditor share-link manager" sub={d.shares?.note} testid="a9-shares">
        <table className="k-table"><thead><tr><th>Token</th><th>Project</th><th>Expires</th><th></th></tr></thead><tbody>
          {d.shares?.links?.map(l => (
            <tr key={l.token}><td className="mono" style={{ fontSize: 11 }}>{String(l.token).slice(0, 10)}…</td><td className="k-muted">{l.project_id}</td>
              <td className="k-muted">{String(l.expires_at || "").slice(0, 10)}</td>
              <td style={{ display: "flex", gap: 6 }}>
                <CopyButton value={l.token} label="Copy link" testid={`share-copy-${l.token}`} style={{ padding: "4px 8px", fontSize: 12 }} />
                <button className="k-btn k-btn-ghost" data-testid={`share-revoke-${l.token}`} style={{ padding: "4px 8px", fontSize: 12 }} onClick={() => revokeShare(l.token)}>Revoke</button>
              </td></tr>
          ))}
        </tbody></table>
        {(!d.shares?.links || d.shares.links.length === 0) && <p className="k-muted" style={{ fontSize: 13 }}>No active share links.</p>}
      </Card>
      <Card title="A14 · Reviewer seat utilisation" sub={d.seats?.note} testid="a14-seats">
        <p className="k-muted" style={{ fontSize: 13 }}>Total seats: {d.seats?.total_seats ?? 0}</p>
        <KV obj={d.seats?.by_domain} />
      </Card>
      <Card title="A15 · Perimeter Gate drop-off" sub={d.dropoff?.note} testid="a15-dropoff">
        <p className="k-muted" style={{ fontSize: 13 }}>Assessments completed: {d.dropoff?.assessments_completed ?? 0} · questions tracked: {d.dropoff?.questions?.length ?? 0}</p>
      </Card>
      <Card title="A10 · Market readiness review" sub={d.readiness?.note} testid="a10-readiness">
        <div style={{ maxHeight: 280, overflowY: "auto" }}>
          <table className="k-table"><thead><tr><th>Region</th><th>Depth</th><th>Live</th><th>Annotation</th><th></th></tr></thead><tbody>
            {d.readiness?.regions?.map((r, i) => (
              <tr key={i}><td className="k-heading">{r.code}</td><td>{String(r.declared_depth)}</td><td>{r.live_connectors}</td>
                <td className="k-muted">{r.admin_annotation || "—"}</td>
                <td><button className="k-btn k-btn-ghost" data-testid={`readiness-annotate-${r.code}`} style={{ padding: "4px 8px", fontSize: 12 }} onClick={() => annotate(r.code)}>Annotate</button></td></tr>
            ))}
          </tbody></table>
        </div>
      </Card>
      <Card title="A11 · 'What Kadana will not do yet' content" sub="Editable honesty-step content (BW11)." testid="a11-honesty">
        {d.honesty?.items?.map(it => <div key={it.id} className="k-muted" style={{ fontSize: 13, marginBottom: 6 }} data-testid={`honesty-${it.id}`}>• {it.text} {!it.active && <span className="k-tag-warn" style={{ fontSize: 10 }}>hidden</span>}</div>)}
        <button className="k-btn k-btn-ghost" data-testid="honesty-add" style={{ marginTop: 8 }} onClick={addHonesty}>Add item</button>
      </Card>
    </>
  );
}

// ---------------- A19 Margin simulator ----------------
function MarginSim() {
  const [f, setF] = useState({ feature_name: "", est_llm_credits_per_use: 0, expected_uses_per_month: 0, est_extra_revenue_credits_per_month: 0 });
  const [res, setRes] = useState(null);
  const run = async () => {
    try { const r = await api.post(`${C}/margin-simulator`, { ...f, est_llm_credits_per_use: Number(f.est_llm_credits_per_use), expected_uses_per_month: Number(f.expected_uses_per_month), est_extra_revenue_credits_per_month: Number(f.est_extra_revenue_credits_per_month) }); setRes(r.data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="A19 · Margin-impact simulator (pre-flight)" sub="Estimate a proposed patch's margin impact against the floor BEFORE it ships." testid="a19-margin-sim">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <input className="k-control" data-testid="sim-name" placeholder="feature name" style={{ minWidth: 160 }} value={f.feature_name} onChange={e => setF({ ...f, feature_name: e.target.value })} />
        <input className="k-control" data-testid="sim-cost" type="number" placeholder="LLM credits/use" style={{ maxWidth: 150 }} value={f.est_llm_credits_per_use} onChange={e => setF({ ...f, est_llm_credits_per_use: e.target.value })} />
        <input className="k-control" data-testid="sim-uses" type="number" placeholder="uses/month" style={{ maxWidth: 140 }} value={f.expected_uses_per_month} onChange={e => setF({ ...f, expected_uses_per_month: e.target.value })} />
        <input className="k-control" data-testid="sim-rev" type="number" placeholder="extra revenue credits/mo" style={{ maxWidth: 190 }} value={f.est_extra_revenue_credits_per_month} onChange={e => setF({ ...f, est_extra_revenue_credits_per_month: e.target.value })} />
        <button className="k-btn k-btn-primary" data-testid="sim-run" onClick={run}>Simulate</button>
      </div>
      {res && (
        <div className="k-control" data-testid="sim-result">
          <p className="k-heading" style={{ fontSize: 14 }}>Verdict: {res.verdict}</p>
          <p className="k-muted" style={{ fontSize: 13 }}>Floor {res.margin_floor_pct}% · projected feature margin {res.projected_feature_margin_pct ?? "n/a"}% · added cost {res.added_cost_credits_per_month}/mo · added revenue {res.added_revenue_credits_per_month}/mo</p>
        </div>
      )}
    </Card>
  );
}

const TABS = [
  ["queue", "Verification Queue (A1)", VerifyQueue],
  ["data", "Catalogue & Data (A2-A4,A12,A13,A18,A20)", CatalogueData],
  ["journey", "Journey Analytics (A5-A11,A14-A17)", JourneyAnalytics],
  ["margin", "Margin Simulator (A19)", MarginSim],
];

export default function AdminGovernance() {
  const [tab, setTab] = useState("queue");
  const Active = TABS.find(t => t[0] === tab)[2];
  return (
    <Shell active="admin">
      <h1 className="k-heading" style={{ fontSize: 28, marginTop: 0 }}>Menu 11 — Catalogue & Data Governance</h1>
      <p className="k-muted" style={{ fontSize: 13, marginTop: 0 }}>Patch 30 · the verification queue every connector patch assumed, plus 19 governance & journey tools.</p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "14px 0 20px" }}>
        {TABS.map(([k, label]) => (
          <button key={k} className={tab === k ? "k-btn k-btn-primary" : "k-btn k-btn-ghost"} data-testid={`tab-${k}`} onClick={() => setTab(k)}>{label}</button>
        ))}
      </div>
      <Active />
    </Shell>
  );
}
